import React, { useState } from 'react';
import { BarChart3, Check, X, AlertCircle, Eye, EyeOff, ChevronDown, ChevronUp, User } from 'lucide-react';
import { SimulationResults } from '../types/api';
import { cn, formatPercentage, formatNumber } from '../lib/utils';

interface ResultsDisplayProps {
  results: SimulationResults | null;
  allResponses?: SimulationResults['sample_responses'];
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results, allResponses }) => {
  const [showDetailedResponses, setShowDetailedResponses] = useState(false);
  const [showAllResponses, setShowAllResponses] = useState(false);
  const [expandedProfiles, setExpandedProfiles] = useState<Set<number>>(new Set());

  if (!results) {
    return (
      <div className="rounded-lg border bg-card p-8">
        <div className="text-center text-muted-foreground">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Run a simulation to see results</p>
        </div>
      </div>
    );
  }

  const validResponses = results.si_count + results.no_count;
  const siPercentage = validResponses > 0 ? (results.si_count / validResponses) * 100 : 0;
  const noPercentage = validResponses > 0 ? (results.no_count / validResponses) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="rounded-lg border bg-card p-5">
        <div className="flex items-center space-x-2 mb-5">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Results</h2>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {/* Response Distribution */}
          <div className="rounded-md border p-3">
            <div className="flex items-center space-x-1.5 mb-2">
              <Check className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-xs font-medium">Yes</h3>
            </div>
            <div className="text-xl font-semibold">{formatNumber(results.si_count)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{siPercentage.toFixed(1)}%</div>
          </div>

          <div className="rounded-md border p-3">
            <div className="flex items-center space-x-1.5 mb-2">
              <X className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-xs font-medium">No</h3>
            </div>
            <div className="text-xl font-semibold">{formatNumber(results.no_count)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{noPercentage.toFixed(1)}%</div>
          </div>

          <div className="rounded-md border p-3">
            <div className="flex items-center space-x-1.5 mb-2">
              <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-xs font-medium">Invalid</h3>
            </div>
            <div className="text-xl font-semibold">{formatNumber(results.other_count)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {((results.other_count / results.total_responses) * 100).toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Visual Bar Chart */}
        <div className="mb-6">
          <h3 className="text-xs font-medium text-muted-foreground mb-3">Distribution</h3>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium">Yes</span>
                <span className="text-muted-foreground">{formatNumber(results.si_count)}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="bg-foreground h-full rounded-full transition-all duration-500"
                  style={{ width: `${siPercentage}%` }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium">No</span>
                <span className="text-muted-foreground">{formatNumber(results.no_count)}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="bg-foreground/60 h-full rounded-full transition-all duration-500"
                  style={{ width: `${noPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Accuracy Metrics */}
        {results.accuracy !== null && results.accuracy !== undefined && (
          <div className="border-t pt-5">
            <h3 className="text-xs font-medium text-muted-foreground mb-3">Validation</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="rounded-md border p-2.5">
                <div className="text-[10px] text-muted-foreground mb-0.5">Accuracy</div>
                <div className="text-lg font-semibold">
                  {formatPercentage(results.accuracy)}
                </div>
              </div>
              
              {results.precision_si !== null && results.precision_si !== undefined && (
                <div className="rounded-md border p-2.5">
                  <div className="text-[10px] text-muted-foreground mb-0.5">Precision (Yes)</div>
                  <div className="text-lg font-semibold">
                    {formatPercentage(results.precision_si)}
                  </div>
                </div>
              )}
              
              {results.recall_si !== null && results.recall_si !== undefined && (
                <div className="rounded-md border p-2.5">
                  <div className="text-[10px] text-muted-foreground mb-0.5">Recall (Yes)</div>
                  <div className="text-lg font-semibold">
                    {formatPercentage(results.recall_si)}
                  </div>
                </div>
              )}
              
              {results.f1_si !== null && results.f1_si !== undefined && (
                <div className="rounded-md border p-2.5">
                  <div className="text-[10px] text-muted-foreground mb-0.5">F1-Score (Yes)</div>
                  <div className="text-lg font-semibold">
                    {formatPercentage(results.f1_si)}
                  </div>
                </div>
              )}
            </div>

            {/* Ground Truth Distribution */}
            {results.ground_truth_distribution && (
              <div className="mt-4 rounded-md bg-muted/30 p-3">
                <h4 className="text-xs font-medium mb-2">Ground Truth</h4>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                  {Object.entries(results.ground_truth_distribution).map(([key, value]) => (
                    <span key={key}>
                      {key}: {formatNumber(value)} ({((value / Object.values(results.ground_truth_distribution!).reduce((a, b) => a + b, 0)) * 100).toFixed(1)}%)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sample Responses */}
      <div className="rounded-lg border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Responses</h3>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowDetailedResponses(!showDetailedResponses)}
              className="flex items-center space-x-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {showDetailedResponses ? (
                <>
                  <EyeOff className="h-3.5 w-3.5" />
                  <span>Hide details</span>
                </>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5" />
                  <span>Show details</span>
                </>
              )}
            </button>
            {(allResponses && allResponses.length > 5) || results.sample_responses.length > 5 ? (
              <button
                onClick={() => setShowAllResponses(!showAllResponses)}
                className="flex items-center space-x-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {showAllResponses ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    <span>Show less</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    <span>Show all</span>
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {(showAllResponses ? (allResponses || results.sample_responses) : results.sample_responses.slice(0, 5)).map((response, index) => {
            const isExpanded = expandedProfiles.has(response.persona_id);
            
            return (
              <div key={response.persona_id} className="rounded-md border p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-wrap gap-1.5">
                    <span className="text-sm font-medium">
                      #{response.persona_id + 1}
                    </span>
                    <span className={cn(
                      "px-1.5 py-0.5 rounded text-xs font-medium",
                      response.classified_answer === 'si' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" :
                      response.classified_answer === 'no' ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" :
                      "bg-muted text-muted-foreground"
                    )}>
                      {response.classified_answer}
                    </span>
                    {response.ground_truth && (
                      <span className={cn(
                        "px-1.5 py-0.5 rounded text-xs font-medium flex items-center space-x-0.5",
                        response.classified_answer === response.ground_truth
                          ? 'bg-foreground text-background'
                          : 'bg-destructive/20 text-destructive'
                      )}>
                        {response.classified_answer === response.ground_truth ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        <span>truth: {response.ground_truth}</span>
                      </span>
                    )}
                  </div>
                  
                  {response.persona_profile && (
                    <button
                      onClick={() => {
                        const newSet = new Set(expandedProfiles);
                        if (isExpanded) {
                          newSet.delete(response.persona_id);
                        } else {
                          newSet.add(response.persona_id);
                        }
                        setExpandedProfiles(newSet);
                      }}
                      className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <User className="h-3 w-3" />
                      <span>{isExpanded ? 'Hide' : 'Show'} profile</span>
                    </button>
                  )}
                </div>
                
                {/* Persona Profile */}
                {isExpanded && response.persona_profile && (
                  <div className="mt-2 rounded bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-2.5 text-xs text-blue-900 dark:text-blue-100 whitespace-pre-wrap">
                    <div className="font-medium mb-1 text-blue-700 dark:text-blue-300">Input Profile:</div>
                    {response.persona_profile}
                  </div>
                )}
                
                {/* AI Response */}
                {showDetailedResponses && (
                  <div className="mt-2 rounded bg-muted/30 p-2.5 text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                    <div className="font-medium mb-1 text-foreground">AI Response:</div>
                    {response.full_response}
                  </div>
                )}
                
                {/* Reasoning if available */}
                {showDetailedResponses && response.reasoning && (
                  <div className="mt-2 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-2.5 text-xs text-amber-900 dark:text-amber-100 whitespace-pre-wrap">
                    <div className="font-medium mb-1 text-amber-700 dark:text-amber-300">Reasoning:</div>
                    {response.reasoning}
                  </div>
                )}
              </div>
            );
          })}
          
          {!showAllResponses && results.sample_responses.length > 5 && (
            <button
              onClick={() => setShowAllResponses(true)}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-2 transition-colors"
            >
              + {(allResponses?.length || results.sample_responses.length) - 5} more responses
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;
